import { TagSeverity } from '@/components/chariot-ui';

import type { DropdownOption } from '@/types/dropdown.types';
import {
  PRIORITY_TO_SEVERITY_MAP,
  VULNERABILITY_FIELDS,
} from '@/utils/mappings.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { getPastTense } from '@/utils/text.util';

/**
 * Severity mapping from priority number to a user-friendly label.
 * This definition is centralized here to be used across the application.
 */
export const SEVERITY_DEFINITION: Record<number, string> =
  PRIORITY_TO_SEVERITY_MAP;

/**
 * A centralized array of severity options for use in dropdowns and filters.
 */
export const SEVERITY_OPTIONS: DropdownOption[] = [
  {
    label: 'Critical',
    value: 'C',
    severity: 'critical' as TagSeverity,
  },
  {
    label: 'High',
    value: 'H',
    severity: 'high' as TagSeverity,
  },
  {
    label: 'Medium',
    value: 'M',
    severity: 'medium' as TagSeverity,
  },
  {
    label: 'Low',
    value: 'L',
    severity: 'low' as TagSeverity,
  },
  {
    label: 'Info',
    value: 'I',
    severity: 'info' as TagSeverity,
  },
  {
    label: 'Exposure',
    value: 'E',
    severity: 'exposure' as TagSeverity,
  },
];

/**
 * Converts a vulnerability's numeric priority to its corresponding severity label.
 *
 * Expected priority values from backend:
 * - 0: Critical
 * - 10: High
 * - 20: Medium
 * - 30: Low
 * - 40: Info
 * - 50: Exposure
 *
 * @param priority - The numeric priority of the vulnerability.
 * @returns The capitalized severity label (e.g., "Critical").
 */
export const getVulnerabilitySeverityLabel = (priority: number): string => {
  // Provide fallback for unexpected values
  if (priority === null || priority === undefined) {
    return 'Unknown';
  }

  return PRIORITY_TO_SEVERITY_MAP[priority] || `Priority ${priority}`;
};

/**
 * Converts a vulnerability's numeric priority to a format suitable for the RiskTag component.
 *
 * @param priority - The numeric priority of the vulnerability.
 * @returns The lowercase severity label (e.g., "critical").
 */
export const getVulnerabilitySeverityTag = (priority: number): TagSeverity => {
  const label = getVulnerabilitySeverityLabel(priority);

  // Map known labels to valid TagSeverity values
  const validSeverities: Record<string, TagSeverity> = {
    Critical: 'critical',
    High: 'high',
    Medium: 'medium',
    Low: 'low',
    Info: 'info',
    Exposure: 'exposure',
  };

  return validSeverities[label] || 'info'; // Default to 'info' for unknown severities
};

/**
 * Converts a vulnerability status code into a user-friendly, past-tense label for display.
 *
 * Expected status formats from backend:
 * - Single character codes: 'P' (Pending), 'O' (Open), 'I' (Accepted), 'R' (Closed), 'T' (Triage), 'D' (Deleted)
 * - Two character codes: 'TH' (Triage High), 'OC' (Open Critical), etc.
 * - Full status strings: "PENDING", "OPEN", "ACCEPTED", "CLOSED"
 *
 * @param status - The status string, where the first character is the code (e.g., 'A', 'I', 'D', 'R').
 * @returns The formatted status label.
 */
export const getVulnerabilityStatusLabel = (status: string): string => {
  if (!status) {
    return 'Unknown';
  }

  // Handle full status strings (fallback for unexpected formats)
  const fullStatusMap: Record<string, string> = {
    PENDING: 'Pending',
    OPEN: 'Open',
    ACCEPTED: 'Accepted',
    CLOSED: 'Closed',
    TRIAGE: 'Pending',
    DELETED: 'Deleted',
  };

  const upperStatus = status.toUpperCase();
  if (fullStatusMap[upperStatus]) {
    const label = fullStatusMap[upperStatus];
    // Apply past tense for certain statuses
    if (['ACCEPTED', 'CLOSED', 'DELETED'].includes(upperStatus)) {
      return getPastTense(label);
    }
    return label;
  }

  // Handle status codes (existing logic)
  const statusCode = status?.[0] || '';
  const statusInfo = getRiskStatusLabel(statusCode);
  let label = statusInfo.statusLabel;

  // Use past tense for certain statuses for display consistency
  if (['I', 'D', 'R'].includes(statusCode)) {
    label = getPastTense(label);
  }

  return label || status;
};

// Re-export VULNERABILITY_FIELDS from mappings.util.ts for backward compatibility
export { VULNERABILITY_FIELDS };
